import BillingFollowupWarning from '../../../components/BillingFollowupWarning';
import BillingDocumentStatusPanel from '../../../components/BillingDeliveries/BillingDocumentStatusPanel';
import { ClientOnboardingWarning } from '../../../components/ClientOnboardingWarning';
import { Avatar, Box, Button, Chip, Drawer, IconButton, Stack, Typography } from "@mui/material";
import {
  CheckCircleRounded,
  Close,
  OpenInNew,
  RadioButtonUncheckedRounded,
  WarningAmberRounded,
} from "@mui/icons-material";
import dayjs from "dayjs";
import { safeAvatarSrc } from "../_components/dashboardCoordinators";
import HighlightStar from "../_components/HighlightStar";
import ReadinessBar from "./ReadinessBar";
import MoneyBars from "./MoneyBars";
import {
  barLook,
  clientLabel,
  dateRangeLabel,
  LIFECYCLE_LABELS,
  money as usd,
  moneyColor,
  outlinedIconSx,
  placeLabel,
  readinessColor,
  SEVERITY_COLORS,
  THEME,
  whenLabel,
} from "./boardStyles";
import type { BoardPerson, BoardShow, ShowMoney } from "./boardTypes";
import { duplicateMarkedLabel } from "../../../store/actions/eventDuplicate";

type Props = {
  show: BoardShow | null;
  onClose: () => void;
  onOpenShow: (show: BoardShow) => void;
  /** Star in the header (absent = no star). */
  highlighted?: boolean;
  onToggleHighlight?: (show: BoardShow) => void;
};

function initials(name: string): string {
  const parts = name.split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  return `${parts[0][0] ?? ""}${parts.length > 1 ? parts[parts.length - 1][0] ?? "" : ""}`.toUpperCase();
}

function Person({ person, role }: { person: BoardPerson; role?: string }) {
  return (
    <Stack direction="row" spacing={1.25} alignItems="center">
      <Avatar
        src={safeAvatarSrc(person.profileImage)}
        alt={person.fullName}
        sx={{ width: 36, height: 36, fontSize: "14px", fontWeight: 700, bgcolor: "#333", color: "#fff", border: "2px solid #000" }}
      >
        {initials(person.fullName || person.email || "?")}
      </Avatar>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: "15px" }}>{person.fullName || person.email}</Typography>
        {role ? <Typography sx={{ fontSize: "13px", opacity: 0.65 }}>{role}</Typography> : null}
      </Box>
    </Stack>
  );
}

const sectionTitleSx = { fontSize: "15px", fontWeight: 600, color: THEME.pink, mb: "10px" };

function Line({ label, value }: { label: string; value: string }) {
  return (
    <Stack direction="row" justifyContent="space-between" spacing={2}>
      <Typography sx={{ fontSize: "15px", opacity: 0.75 }}>{label}</Typography>
      <Typography sx={{ fontSize: "15px", textAlign: "right" }}>{value}</Typography>
    </Stack>
  );
}

/** Payments (admins): client and crew, and whether the show is finalized. */
function PaymentsSection({ show, value }: { show: BoardShow; value: ShowMoney }) {
  const { client, crew } = value;
  const color = moneyColor(show);
  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", mb: "10px" }}>
        <Typography sx={{ ...sectionTitleSx, mb: 0 }}>Payments</Typography>
        <Typography sx={{ fontSize: "15px", fontWeight: 600, color }}>
          {value.finalized ? "Finalized" : show.timing === "past" ? "Not finalized" : "In progress"}
        </Typography>
      </Box>
      {value.issues.length ? (
        <Box sx={{ mb: "12px", padding: "10px 12px", borderRadius: "12px", border: "1px solid #FD355A", borderLeft: "6px solid #FD355A" }}>
          {value.reasons.slice(0, value.issues.length).map((r) => (
            <Typography key={r} sx={{ fontSize: "15px" }}>
              {r}
            </Typography>
          ))}
        </Box>
      ) : null}
      <MoneyBars value={value} />
      <Stack spacing={0.75} sx={{ mt: "12px" }}>
        <Line
          label="Final invoice"
          value={client.finalInvoiceSent ? `Sent${client.dueDate && client.due > 0 ? ` · due ${dayjs(client.dueDate).format("MMM D")}` : ""}` : "Not sent"}
        />
        <Line label="Client paid" value={`${usd(client.paid)} of ${usd(client.billed)}`} />
        <Line
          label="Crew paid"
          value={crew.people ? `${crew.paidPeople}/${crew.people} people · ${usd(crew.paid)} of ${usd(crew.total)}` : "No crew"}
        />
        {crew.people > crew.withPo ? <Line label="POs missing" value={`${crew.people - crew.withPo}`} /> : null}
      </Stack>
    </Box>
  );
}

/** Quick look at one show without leaving the dashboard. */
export default function ShowQuickPanel({
  show,
  onClose,
  onOpenShow,
  highlighted = false,
  onToggleHighlight,
}: Props) {
  return (
    <Drawer
      anchor="right"
      open={!!show}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: "min(440px, 100vw)",
          background: THEME.black,
          borderLeft: `1px solid ${THEME.pink}`,
          boxShadow: THEME.glow,
          color: THEME.white,
        },
      }}
    >
      {show ? (
        <PanelBody
          show={show}
          onClose={onClose}
          onOpenShow={onOpenShow}
          highlighted={highlighted}
          onToggleHighlight={onToggleHighlight}
        />
      ) : null}
    </Drawer>
  );
}

function PanelBody({
  show,
  onClose,
  onOpenShow,
  highlighted,
  onToggleHighlight,
}: {
  show: BoardShow;
  onClose: () => void;
  onOpenShow: (s: BoardShow) => void;
  highlighted: boolean;
  onToggleHighlight?: (s: BoardShow) => void;
}) {
  const look = barLook(show);
  const days = show.startDay && show.endDay ? dayjs(show.endDay).diff(dayjs(show.startDay), "day") + 1 : null;
  const place = [show.venue, placeLabel(show)].filter(Boolean).join(" · ");
  const attention = show.attention && {
    ...show.attention,
    reasons: show.attention.reasons.filter(reason => !show.clientOnboarding?.reasons.includes(reason)),
  };
  const isDraft = show.lifecycle === "draft";

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Box sx={{ padding: "20px 20px 16px", borderBottom: `1px solid ${THEME.pinkSoft}` }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px" }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Chip
              size="small"
              label={LIFECYCLE_LABELS[show.lifecycle]}
              sx={{
                bgcolor: look.bg,
                color: look.fg,
                fontWeight: 700,
                border: look.border ?? "none",
              }}
            />
            <Typography sx={{ fontSize: "14px", opacity: 0.8 }}>{whenLabel(show)}</Typography>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            {onToggleHighlight ? (
              <HighlightStar on={highlighted} showName={show.eventName} onToggle={() => onToggleHighlight(show)} size={24} />
            ) : null}
            <IconButton aria-label="Close" onClick={onClose} sx={outlinedIconSx}>
              <Close />
            </IconButton>
          </Stack>
        </Box>
        <Typography
          variant="h4"
          component="h2"
          sx={{ mt: "12px", lineHeight: 1.2, textDecoration: look.strike ? "line-through" : "none" }}
        >
          {show.eventName}
        </Typography>
        {show.isDuplicate ? (
          <Typography sx={{ mt: "6px", fontSize: "14px", color: "#FACE70" }}>
            {duplicateMarkedLabel(show.duplicateMarked?.by, show.duplicateMarked?.at)}
          </Typography>
        ) : null}
        {show.jobNumber ? (
          <Typography sx={{ fontSize: "14px", opacity: 0.65 }}>Job #{show.jobNumber}</Typography>
        ) : null}
        <Stack spacing={0.5} sx={{ mt: "12px" }}>
          {clientLabel(show) ? (
            <Typography sx={{ fontSize: "15px" }}>
              {clientLabel(show)}
              {show.client?.companyName && show.client?.name ? (
                <Box component="span" sx={{ opacity: 0.65 }}> · {show.client.name}</Box>
              ) : null}
            </Typography>
          ) : null}
          <Typography sx={{ fontSize: "15px", opacity: 0.8 }}>
            {dateRangeLabel(show)}
            {days ? ` · ${days} day${days === 1 ? "" : "s"}` : ""}
          </Typography>
          {place ? <Typography sx={{ fontSize: "15px", opacity: 0.8 }}>{place}</Typography> : null}
        </Stack>
      </Box>

      <Box sx={{ padding: "18px 20px", overflowY: "auto", flex: 1, display: "flex", flexDirection: "column", gap: "22px" }}>
        {attention?.reasons.length ? (
          <Box
            sx={{
              padding: "12px 14px",
              borderRadius: "12px",
              border: `1px solid ${SEVERITY_COLORS[attention.severity]}`,
              borderLeft: `6px solid ${SEVERITY_COLORS[attention.severity]}`,
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: "6px" }}>
              <WarningAmberRounded sx={{ fontSize: 20, color: SEVERITY_COLORS[attention.severity] }} />
              <Typography sx={{ fontSize: "15px", fontWeight: 600 }}>What is missing</Typography>
            </Stack>
            <Box component="ul" sx={{ margin: 0, paddingLeft: "22px" }}>
              {attention.reasons.map((r) => (
                <Typography component="li" key={r} sx={{ fontSize: "15px" }}>
                  {r}
                </Typography>
              ))}
            </Box>
          </Box>
        ) : null}

        <ClientOnboardingWarning status={show.clientOnboarding} />
        <BillingFollowupWarning status={show.finalInvoiceFollowup} eventId={show._id} approved={show.approved} />
        <BillingFollowupWarning status={show.depositFollowup} eventId={show._id} approved={show.approved} />
        <BillingDocumentStatusPanel eventId={show._id} />

        {show.money && show.timing === "past" ? <PaymentsSection show={show} value={show.money} /> : null}

        <Box>
          <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", mb: "8px" }}>
            <Typography sx={{ ...sectionTitleSx, mb: 0 }}>Readiness</Typography>
            <Typography sx={{ fontSize: "30px", fontWeight: 600, color: readinessColor(show), lineHeight: 1 }}>
              {show.readiness.percent}%
            </Typography>
          </Box>
          <ReadinessBar show={show} withCrew={false} withPercent={false} height={8} />
          <Stack component="ol" spacing={1.25} sx={{ listStyle: "none", padding: 0, margin: "16px 0 0" }}>
            {show.readiness.steps.map((step) => (
              <Stack component="li" key={step.key} direction="row" spacing={1.25} alignItems="center">
                {step.done ? (
                  <CheckCircleRounded sx={{ fontSize: 22, color: THEME.green }} />
                ) : (
                  <RadioButtonUncheckedRounded sx={{ fontSize: 22, opacity: 0.5 }} />
                )}
                <Typography sx={{ fontSize: "15px", flex: 1, opacity: step.done ? 1 : 0.75 }}>{step.label}</Typography>
                {step.detail ? (
                  <Typography sx={{ fontSize: "14px", opacity: 0.65, textAlign: "right" }}>{step.detail}</Typography>
                ) : null}
              </Stack>
            ))}
          </Stack>
        </Box>

        {show.money && show.timing !== "past" ? <PaymentsSection show={show} value={show.money} /> : null}

        <Box>
          <Typography sx={sectionTitleSx}>Team</Typography>
          <Stack spacing={1.25}>
            {show.coordinators.length ? (
              show.coordinators.map((c) => <Person key={c._id} person={c} role="Coordinator" />)
            ) : (
              <Typography sx={{ fontSize: "15px", opacity: 0.65 }}>No coordinator assigned</Typography>
            )}
            {show.createdBy ? <Person person={show.createdBy} role="Built the show" /> : null}
          </Stack>
        </Box>
      </Box>

      <Box sx={{ padding: "16px 20px", borderTop: `1px solid ${THEME.pinkSoft}`, display: "flex", flexDirection: "column", gap: "10px" }}>
        <Button fullWidth endIcon={<OpenInNew />} onClick={() => onOpenShow(show)}>
          {isDraft ? "Open saved drafts" : "Open show"}
        </Button>
      </Box>
    </Box>
  );
}
